// src/pages/LevelPage.js
import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ResponsePopup from '../components/ResponsePopup';
import StudentList from '../components/StudentList';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import AssignmentList from '../components/AssignmentList';

export default function LevelPage() {
  const { levelCode } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResponsePopup, setShowResponsePopup] = useState(false);
  const [selectedUpdateId, setSelectedUpdateId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('grades');

  // Get user data from session storage
  useEffect(() => {
    const authData = sessionStorage.getItem('user_auth');
    if (authData) {
      const { user } = JSON.parse(authData);
      setUserData(user);
      setIsTeacher(user.role === 'instructor' || user.role === 'admin');
    }
  }, []);

  // Check enrollment and fetch data when component mounts
  useEffect(() => {
    if (userData) {
      checkEnrollment();
    }
  }, [userData]);

  const checkEnrollment = async () => {
    try {
      setLoading(true);
      
      // If user is a teacher or admin, they have access to all levels
      if (isTeacher) {
        setIsEnrolled(true);
        await fetchData();
        return;
      }

      // For students, check enrollment
      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from('student_enrollment')
        .select('*')
        .eq('level_code', levelCode)
        .eq('student_id', userData.id)
        .single();

      if (enrollmentError || !enrollmentData) {
        setError('You are not enrolled in this class. Please contact your teacher to get access.');
        setLoading(false);
        return;
      }

      setIsEnrolled(true);
      await fetchData();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch level data
      const { data: classData, error: classError } = await supabase
        .from('levels')
        .select('*')
        .eq('code', levelCode)
        .single();

      if (classError) throw classError;

      // Fetch updates
      const { data: updatesData, error: updatesError } = await supabase
        .from('updates')
        .select('*')
        .eq('level_code', levelCode)
        .order('created_at', { ascending: false });

      if (updatesError) throw updatesError;

      // Fetch responses
      const updateIds = updatesData?.map(u => u.id) || [];
      if (updateIds.length > 0) {
        const { data: responsesData, error: responsesError } = await supabase
          .from('responses')
          .select('*, student:users(name)')
          .in('update_id', updateIds)
          .order('created_at', { ascending: false });

        if (responsesError) throw responsesError;
        setResponses(responsesData || []);
      }

      // Fetch grades for this student and level
      if (!isTeacher && userData) {
        const { data: gradesData, error: gradesError } = await supabase
          .from('grades')
          .select(`
            id,
            score,
            feedback,
            graded_at,
            excused,
            assignment:assignments!inner (
              id,
              title,
              max_score,
              level_code,
              due_date,
              assignment_type,
              category:grade_categories (
                name
              )
            )
          `)
          .eq('student_id', userData.id)
          .eq('assignments.level_code', levelCode)
          .order('graded_at', { ascending: false });

        if (gradesError) throw gradesError;
        setGrades(gradesData || []);

        // Fetch attendance for this student
        const { data: attendanceData, error: attendanceError } = await supabase
          .from('attendance')
          .select('*')
          .eq('student_id', userData.id)
          .eq('level_code', levelCode)
          .order('date', { ascending: false });

        if (attendanceError) throw attendanceError;
        setAttendance(attendanceData || []);
      }

      setClassData(classData);
      setUpdates(updatesData || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch responses for a specific update
  const fetchResponses = async (updateId) => {
    try {
      const { data, error } = await supabase
        .from('responses')
        .select('*, student:users(name)')
        .eq('update_id', updateId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setResponses(prev => [
        ...prev.filter(r => r.update_id !== updateId),
        ...data
      ]);
    } catch (err) {
      console.error('Error fetching responses:', err);
    }
  };

  // Calculate letter grade
  const calculateLetterGrade = (percentage) => {
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  };

  // Group grades by assignment type (must be before early returns for hooks rules)
  const GRADE_TYPE_ORDER = ['exam', 'quiz', 'hw', 'cw', 'others'];
  const GRADE_TYPE_LABELS = { exam: 'Exam', quiz: 'Quiz', hw: 'Homework', cw: 'Classwork', others: 'Others' };
  const gradesByType = useMemo(() => {
    const byType = {};
    GRADE_TYPE_ORDER.forEach((t) => { byType[t] = []; });
    (grades || []).forEach((g) => {
      const t = g.assignment?.assignment_type || 'others';
      if (!byType[t]) byType[t] = [];
      byType[t].push(g);
    });
    return GRADE_TYPE_ORDER.map((type) => ({ type, list: byType[type] || [] })).filter((g) => g.list.length > 0);
  }, [grades]);

  // Calculate overall grade statistics (excused assignments excluded)
  const getGradeStats = () => {
    const counted = grades.filter(g => !g.excused);
    if (counted.length === 0) return { average: 0, totalAssignments: 0, completedAssignments: 0 };

    const totalScore = counted.reduce((sum, grade) => sum + (grade.score || 0), 0);
    const maxPossible = counted.reduce((sum, grade) => sum + (grade.assignment?.max_score || 0), 0);
    const average = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;

    return {
      average,
      totalAssignments: counted.length,
      completedAssignments: counted.filter(g => g.score !== null).length
    };
  };

  // Calculate attendance statistics
  const getAttendanceStats = () => {
    if (attendance.length === 0) return { rate: 0, total: 0, present: 0 };
    
    const present = attendance.filter(a => a.status === 'present').length;
    const rate = attendance.length > 0 ? Math.round((present / attendance.length) * 100) : 0;
    
    return {
      rate,
      total: attendance.length,
      present
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 animate-fade-in">
          <div className="w-16 h-16 loading-spinner"></div>
          <div className="text-center">
            <p className="text-primary-600 font-semibold text-lg">Loading Class...</p>
            <p className="text-neutral-500 text-sm mt-1">Fetching your class information</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-neutral-50 flex items-center justify-center container-mobile">
        <div className="card p-8 max-w-md w-full text-center animate-slide-up">
          <div className="w-16 h-16 bg-error-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Access Denied</h2>
          <p className="text-neutral-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary w-full"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-neutral-50 flex items-center justify-center container-mobile">
        <div className="card p-8 max-w-md w-full text-center animate-slide-up">
          <div className="w-16 h-16 bg-warning-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Class Not Found</h2>
          <p className="text-neutral-600">The requested class could not be found.</p>
        </div>
      </div>
    );
  }

  const gradeStats = getGradeStats();
  const attendanceStats = getAttendanceStats();

  // Define tabs: student view order = Grades, Attendance, Assignments, Updates; teacher gets Students last
  const tabs = [
    { id: 'grades', label: 'Grades', icon: '📊' },
    { id: 'attendance', label: 'Attendance', icon: '📅' },
    { id: 'assignments', label: 'Assignments', icon: '📝' },
    { id: 'updates', label: 'Updates', icon: '📢' },
    ...(isTeacher ? [{ id: 'students', label: 'Students', icon: '👥' }] : [])
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header - simplified like TeacherDashboard */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-neutral-600 hover:text-neutral-900"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-neutral-900">{classData.name}</h1>
                {classData.period && <p className="text-xs sm:text-sm text-neutral-500">Period: {classData.period}</p>}
              </div>
            </div>
          </div>

          {/* Quick Stats for Students */}
          {!isTeacher && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-2 sm:p-3 bg-neutral-50 rounded-lg">
                <div className="text-base sm:text-lg font-bold text-primary-600">{gradeStats.average}%</div>
                <div className="text-xs text-neutral-600">Grade</div>
              </div>
              <div className="text-center p-2 sm:p-3 bg-neutral-50 rounded-lg">
                <div className="text-base sm:text-lg font-bold text-warning-600">{attendanceStats.rate}%</div>
                <div className="text-xs text-neutral-600">Attendance</div>
              </div>
            </div>
          )}

          {/* Tabs - simplified */}
          <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'updates' && (
          <section className="animate-fade-in">
            {updates.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2">No updates yet</h3>
                <p className="text-neutral-600">Your teacher hasn't posted any updates for this class.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {updates.map((update, index) => (
                  <div key={update.id} className="bg-white border border-neutral-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm sm:text-base font-semibold text-neutral-900">{update.title}</h3>
                      <span className="text-xs text-neutral-500 whitespace-nowrap ml-3">
                        {new Date(update.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    
                    <div className="prose prose-sm max-w-none mb-3">
                      <p className="text-xs sm:text-sm text-neutral-700 whitespace-pre-line leading-relaxed">{update.content}</p>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
                      <button
                        onClick={() => {
                          setSelectedUpdateId(update.id);
                          setShowResponsePopup(true);
                        }}
                        className="bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-1.5"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Add Response
                      </button>
                      
                      <span className="text-xs text-neutral-500">
                        {responses.filter(r => r.update_id === update.id).length} response(s)
                      </span>
                    </div>
                    
                    {/* Display Responses */}
                    {responses.filter(response => response.update_id === update.id).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {responses.filter(response => response.update_id === update.id)
                          .map(response => (
                            <div key={response.id} className="bg-neutral-50 rounded-lg p-2 sm:p-3">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                                    <span className="text-primary-600 font-semibold text-xs">
                                      {response.student?.name?.charAt(0)}
                                    </span>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-neutral-900 text-xs sm:text-sm">{response.title}</h4>
                                    <p className="text-xs text-neutral-500">
                                      {response.student?.name || 'Unknown'} • {new Date(response.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="prose prose-sm max-w-none text-xs sm:text-sm">
                                <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                                  {response.content}
                                </ReactMarkdown>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'assignments' && (
          <section className="animate-fade-in">
            <AssignmentList 
              levelCode={levelCode} 
              studentId={userData?.id}
              isTeacher={isTeacher}
              teacherId={userData?.id}
              name={userData?.name}
            />
          </section>
        )}

        {activeTab === 'grades' && !isTeacher && (
          <section className="animate-fade-in">
            {grades.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2">No grades available</h3>
                <p className="text-neutral-600">Your grades will appear here once assignments are graded.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {gradesByType.map(({ type, list }) => (
                  <div key={type}>
                    <h3 className="text-sm font-semibold text-neutral-600 mb-2 px-1">{GRADE_TYPE_LABELS[type] || type}</h3>
                    <div className="space-y-2">
                      {list.map((grade, index) => {
                        const isExcused = grade.excused;
                        const percentage = !isExcused && grade.assignment?.max_score > 0
                          ? Math.round((grade.score / grade.assignment.max_score) * 100)
                          : 0;
                        const letterGrade = isExcused ? null : calculateLetterGrade(percentage);

                        return (
                          <div key={grade.id} className="bg-white border border-neutral-200 rounded-lg p-3 sm:p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-0.5">{grade.assignment?.title}</h3>
                                {grade.assignment?.category?.name && (
                                  <span className="text-xs text-neutral-500">{grade.assignment.category.name}</span>
                                )}
                              </div>
                              <div className="text-right ml-3">
                                {isExcused ? (
                                  <span className="text-sm font-medium text-neutral-500">Excused</span>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-1.5 mb-1">
                                      <span className="text-lg sm:text-xl font-bold text-neutral-900">{grade.score}</span>
                                      <span className="text-xs sm:text-sm text-neutral-500">/ {grade.assignment?.max_score}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                        letterGrade === 'A' ? 'bg-emerald-100 text-emerald-700' :
                                        letterGrade === 'B' ? 'bg-blue-100 text-blue-700' :
                                        letterGrade === 'C' ? 'bg-amber-100 text-amber-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>
                                        {letterGrade}
                                      </span>
                                      <span className="text-xs sm:text-sm font-medium text-neutral-600">{percentage}%</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {grade.feedback && (
                              <div className="p-2 sm:p-3 bg-neutral-50 rounded-lg mt-2 mb-2">
                                <p className="text-xs sm:text-sm text-neutral-700">{grade.feedback}</p>
                              </div>
                            )}

                            <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
                              <span className="text-xs text-neutral-500">
                                {isExcused ? 'Excused from this assignment' : `Graded on ${new Date(grade.graded_at).toLocaleDateString()}`}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'attendance' && !isTeacher && (
          <section className="animate-fade-in">
            {attendance.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2">No attendance records</h3>
                <p className="text-neutral-600">Your attendance records will appear here.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {attendance.map((record, index) => {
                  const statusColors = {
                    present: { bg: 'bg-emerald-500', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Present' },
                    absent: { bg: 'bg-red-500', text: 'text-red-700', border: 'border-red-200', label: 'Absent' },
                    late: { bg: 'bg-amber-500', text: 'text-amber-700', border: 'border-amber-200', label: 'Late' },
                    excused: { bg: 'bg-blue-500', text: 'text-blue-700', border: 'border-blue-200', label: 'Excused' }
                  };
                  const statusStyle = statusColors[record.status] || statusColors.absent;
                  const date = new Date(record.date);
                  
                  return (
                    <div key={record.id} className={`bg-white border-l-4 ${statusStyle.border} border-t border-r border-b border-neutral-200 rounded-r-lg p-2.5 sm:p-3`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-2.5 h-2.5 rounded-full ${statusStyle.bg} shrink-0`}></div>
                          <div>
                            <span className={`text-xs sm:text-sm font-semibold ${statusStyle.text}`}>
                              {statusStyle.label}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs sm:text-sm font-medium text-neutral-900">
                            {date.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {activeTab === 'students' && isTeacher && (
          <section className="animate-fade-in">
            <StudentList levelCode={classData.code} />
          </section>
        )}
      </main>

      {/* Response Popup */}
      {showResponsePopup && (
        <ResponsePopup 
          updateId={selectedUpdateId}
          onClose={() => setShowResponsePopup(false)}
          studentId={userData?.id}
          onResponseSubmitted={async () => {
            await fetchResponses(selectedUpdateId);
          }}
          name={userData?.name}
        />
      )}
    </div>
  );
}
